import { reduceTwo } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import { getDocURL } from "../../docUrl";
import { setColumnInRowForEditComponent } from "../builder-utils";
import {
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableGlideType,
    getPrimitiveColumns,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { ColumnPropertyFlag, ColumnPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { ComponentBuilder, ComponentConfiguratorContext, ComponentDescriptor } from ".";

const ComponentKindStopwatch: ComponentKind = "stopwatch";

interface StopwatchComponentDescription extends ComponentDescription {
    readonly startTime: PropertyDescription;
    readonly duration: PropertyDescription;
}

const startTimePropertyHandler = new ColumnPropertyHandler(
    "startTime",
    "Start time",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["start", "begin"],
    getPrimitiveColumns,
    "date-time",
    PropertySection.Data
);
const durationPropertyHandler = new ColumnPropertyHandler(
    "duration",
    "Duration",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["duration", "length"],
    getPrimitiveColumns,
    "duration",
    PropertySection.Data
);

export class StopwatchComponentHandler extends ComponentHandlerBase<StopwatchComponentDescription> {
    constructor() {
        super(ComponentKindStopwatch);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: StopwatchComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const startTimeProperty = startTimePropertyHandler.getColumnName(desc);
        const durationProperty = durationPropertyHandler.getColumnName(desc);
        if (startTimeProperty === undefined || durationProperty === undefined) return b.emptyUI;

        const startSetColumns = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            startTimeProperty,
            b.getProperty(startTimeProperty, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext,
            { withDiode: false }
        );
        const durationSetColumns = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            durationProperty,
            b.getProperty(durationProperty, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext,
            { withDiode: false }
        );
        if (startSetColumns === undefined || durationSetColumns === undefined) return b.emptyUI;

        const {
            lets: startTimeLets,
            valueToModify: startTimeValue,
            action: startTimeAction,
            isEnabled: startTimeIsEnabled,
        } = startSetColumns;
        const {
            lets: durationLets,
            valueToModify: durationValue,
            action: durationAction,
            isEnabled: durationIsEnabled,
        } = durationSetColumns;

        if (startTimeValue === undefined || durationValue === undefined) return b.emptyUI;

        const onChange = reduceTwo(startTimeAction, durationAction, (x, y) => b.sequence([x, y]));
        const isEnabled = reduceTwo(startTimeIsEnabled, durationIsEnabled, (x, y) => b.and(x, y));

        return b.let(
            [...startTimeLets, ...durationLets],
            b.ui("Stopwatch", {
                startTime: startTimeValue,
                duration: durationValue,
                onChange,
                isEnabled,
            })
        );
    }

    public getDescriptor(
        _desc: StopwatchComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<StopwatchComponentDescription> {
        return {
            name: "Stopwatch",
            description: "Measure a duration",
            img: "componentStopwatch",
            group: "Entry Fields",
            // FIXME: doc url
            helpUrl: getDocURL("stopwatch"),
            properties: [startTimePropertyHandler, durationPropertyHandler, ...this.getBasePropertyDescriptors()],
        };
    }
}
